import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/env';

export class AuthService {
  static async login(email, password) {
    try {
      // Input validation
      if (!email || !password) {
        return { success: false, error: 'Email and password are required' };
      }

      // Sanitize inputs
      const sanitizedEmail = email.trim().toLowerCase();
      const sanitizedPassword = password.trim();

      // Use the login_verify_user RPC function (same as mobile app)
      // This function bypasses RLS as it's SECURITY DEFINER
      const loginResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/rpc/login_verify_user`,
        {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          },
          body: JSON.stringify({
            p_email: sanitizedEmail,
            p_password: sanitizedPassword
          })
        }
      );

      if (!loginResponse.ok) {
        const errorText = await loginResponse.text();
        console.error('Login RPC error:', loginResponse.status, errorText);
        return { success: false, error: 'Invalid login credentials' };
      }

      const result = await loginResponse.json();
      
      // RPC function returns array, get first result
      const userData = Array.isArray(result) ? result[0] : result;
      
      if (!userData || !userData.id) {
        return { success: false, error: 'Invalid login credentials' };
      }

      // Get user roles
      const roles = await this.getUserRoles(userData.id);
      
      // Normalize role names (handle both formats)
      const normalizeRole = (role) => {
        const normalized = role?.toLowerCase() || '';
        // Map common variations
        if (normalized === 'serviceadvisor') return 'service_advisor';
        if (normalized === 'serviceexecutive') return 'service_executive';
        if (normalized === 'teamlead' || normalized === 'team_lead') return 'teamlead';
        if (normalized === 'tlmanager' || normalized === 'tl_manager') return 'tl_manager';
        if (normalized === 'couriermanager' || normalized === 'courier_manager') return 'courier_manager';
        return normalized;
      };
      
      const normalizedRoles = roles.map(normalizeRole);
      
      // Determine primary role (prefer admin, then management, then service/sales, then field)
      const rolePriority = {
        'admin': 1,
        'supervisor': 2,
        'tl_manager': 3,
        'courier_manager': 4,
        'service_advisor': 5,
        'service_executive': 6,
        'teamlead': 7,
        'telecaller': 8,
        'mechanic': 9,
        'customer': 10,
        'partner': 11
      };
      
      const sortedRoles = normalizedRoles.sort((a, b) => {
        const priorityA = rolePriority[a] || 999;
        const priorityB = rolePriority[b] || 999;
        return priorityA - priorityB;
      });
      
      const primaryRole = sortedRoles[0] || 'customer';

      // call_report_id is expected to be returned by login_verify_user (SECURITY DEFINER),
      // because direct SELECT on users is blocked by RLS for anon in this app.
      const call_report_id = userData?.call_report_id ?? null;
      
      // Create user object with roles
      const userWithRoles = {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        phone: userData.phone || userData.contact_number || '',
        roles: normalizedRoles,
        primaryRole,
        isAdmin: normalizedRoles.some(r => r === 'admin'),
        isSupervisor: normalizedRoles.some(r => r === 'supervisor'),
        isTeamLead: normalizedRoles.some(r => r === 'teamlead'),
        isTelecaller: normalizedRoles.some(r => r === 'telecaller'),
        isServiceAdvisor: normalizedRoles.some(r => r === 'service_advisor'),
        isServiceExecutive: normalizedRoles.some(r => r === 'service_executive'),
        isMechanic: normalizedRoles.some(r => r === 'mechanic'),
        isCustomer: normalizedRoles.some(r => r === 'customer'),
        isTLManager: normalizedRoles.some(r => r === 'tl_manager'),
        isCourierManager: normalizedRoles.some(r => r === 'courier_manager'),
        isPartner: normalizedRoles.some(r => r === 'partner'),
        currentRole: primaryRole,
        call_report_id,
      };

      // Store session in localStorage
      localStorage.setItem('roadserve_current_user', JSON.stringify(userWithRoles));

      return { success: true, user: userWithRoles };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Login failed' };
    }
  }

  static async getUserRoles(userId) {
    try {
      const rolesResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/user_roles?user_id=eq.${userId}&select=roles(name)`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (rolesResponse.ok) {
        const rolesData = await rolesResponse.json();
        return rolesData.map(item => item.roles?.name).filter(Boolean);
      }
      return [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      return [];
    }
  }

  static async logout() {
    try {
      localStorage.removeItem('roadserve_current_user');
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  }

  static async getCurrentUser() {
    try {
      const stored = localStorage.getItem('roadserve_current_user');
      if (stored) {
        const user = JSON.parse(stored);
        return user;
      }
      return null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  static async getSession() {
    try {
      const user = await this.getCurrentUser();
      return user ? { user } : null;
    } catch (error) {
      console.error('Error getting session:', error);
      return null;
    }
  }

  static async forgotPassword(email, oldPassword, newPassword) {
    try {
      // Input validation
      if (!email || !oldPassword || !newPassword) {
        return { success: false, error: 'All fields are required' };
      }

      if (newPassword.length < 6) {
        return { success: false, error: 'New password must be at least 6 characters long' };
      }

      // Sanitize inputs
      const sanitizedEmail = email.trim().toLowerCase();
      const sanitizedOldPassword = oldPassword.trim();
      const sanitizedNewPassword = newPassword.trim();

      // Use the forgot_password_update RPC function (same as mobile app)
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/rpc/forgot_password_update`,
        {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          },
          body: JSON.stringify({
            user_email: sanitizedEmail,
            old_password: sanitizedOldPassword,
            new_password: sanitizedNewPassword
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Forgot password RPC error:', response.status, errorText);
        return { success: false, error: 'Failed to update password. Please check your credentials.' };
      }

      const result = await response.json();
      
      // RPC function returns array, get first result
      const data = Array.isArray(result) ? result[0] : result;
      
      if (data && data.success) {
        return { success: true };
      } else {
        return { success: false, error: data?.message || 'Failed to update password' };
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      return { success: false, error: error.message || 'An error occurred. Please try again.' };
    }
  }
}
